import {
  VehicleClass,
  InvoiceStatus,
  BusStatus,
  UserRole,
  Location,
  SeenStatus,
  RecipientType,
  RideType,
  MessageGroup,
  MessageGroupMember,
  Ride,
  ScheduleElement,
  ContactInfo,
  SeatsRangeEnum,
  ContractStatus,
  RideOfferType,
  OrderV2,
  UploadFile,
  Vehicle,
  MultiStopLegsSchemaType,
  BookingStatus,
  FeaturesEnum,
  ScheduleItemType,
  OwnerType,
  VehicleExtrasEnum,
  BillingFrequency,
} from 'remote/global';
import {
  DeparturesSchemaType,
  CorporateCharterScheduleStopsSchemaType,
} from 'components/forms/CorporateCharter/types';
import { PricingAdjustmentTypeEnum } from './enums';

export type StringOrNumber = string | number;

export interface UserData {
  'husky.operatorId': number;
  sub: string;
  'husky.userId': number;
  nbf: number;
  'husky.roles': string;
  'husky.clientId': number;
  exp: number;
  iat: number;
}

export interface FileResponse {
  fileName: string;
  fileSize: number;
  fileType: string;
  originalFileName: string;
  url: string;
}

export type TypePayload = 'createOffer' | 'sendOrder';

export enum DaysOfWeek {
  MONDAY = 'MONDAY',
  TUESDAY = 'TUESDAY',
  WEDNESDAY = 'WEDNESDAY',
  THURSDAY = 'THURSDAY',
  FRIDAY = 'FRIDAY',
  SATURDAY = 'SATURDAY',
  SUNDAY = 'SUNDAY',
}

export interface SubmitOfferPayload {
  clientEmail?: string;
  discountPct: number;
  endDate: string;
  lang?: string;
  operatorComment?: string;
  rideTypes: RideOfferType[];
}

export interface NewMessagePayload {
  recipientType: RecipientType;
  subject: string;
  content: string;
  userId?: number;
  groupId?: number;
}

export interface ItineraryPayload {
  legs: OfferLeg[];
  passengerCount: number;
}
export interface OrderRequestPayload {
  clientContactEmail: string;
  clientId: number;
  operatorId: number;
  operatorOfferUuid: string;
  returnScheduleItem: boolean;
}

export interface OperatorOfferWithRank {
  createdAt: string;
  currency: string;
  endAddress: string;
  operatorPriceExclVat: number;
  operatorPriceInclVat: number;
  orderType: RideOfferType;
  passengerCount: number;
  rank: number;
  refNumber: string;
  startAddress: string;
  startTime: string;
  totalLengthInKm: number;
  totalOffers: number;
  vatPct: number;
  vehicleClass: VehicleClass;
  vehicleMake: string;
  vehicleModel: string;
}
export interface OperatorOfferByRefNumber {
  chosen: boolean;
  currency: string;
  operatorId: number;
  operatorName: string;
  operatorPriceExclVat: number;
  operatorPriceInclVat: number;
  orderType: RideOfferType;
  priceExclVat: number;
  priceInclVat: number;
  unadjustedPriceExclVat: number;
  unadjustedPriceInclVat: number;
  vatPct: number;
  vehicleClass: VehicleClass;
  vehicleSeats: number;
}

export interface Supplier {
  clientCompanyName: string;
  clientContactName: string;
  clientId: number;
  isSendOfferAvailable: boolean;
  ridesPerQuarter: number;
  supplierContractClientComment: string;
  supplierContractCreatedAt: Date;
  supplierContractDiscountPct: number;
  supplierContractEndDate: Date;
  supplierContractId: number;
  totalHours: number;
  totalKm: number;
}

export interface NewMessageFormValues {
  recipientType: RecipientType;
  to: MessageGroupMember | MessageGroup;
  subject: string;
  content: string;
}

export interface NewUserPayload {
  confirmPassword: string;
  contactInfo: ContactInfo;
  password: string;
  uuid: string;
  lang?: string;
}

export interface VehicleSize {
  seatsFrom: number;
  seatsTo: number;
  seatsRange: string;
}

export interface VehicleTemporaryBase {
  id: number;
  location: Location;
  validFrom: string;
  validTo: string;
  vehicleId: number;
}

export interface Alert {
  bookingNumber: string;
  createdAt: string;
  driverId?: number;
  driverName?: string;
  id: number;
  modifiedAt: string;
  operatorId: number;
  rideId: number;
  status: SeenStatus;
  type: RideType;
  iconUrl?: string;
  iconName?: string;
}

export type AlertCount = {
  operatorId: number;
  count: number;
};

export interface QueryFilter {
  licensePlate?: string | null;
  bookingNumber?: string;
  status?: InvoiceStatus | BusStatus | '';
  invoiceNumber?: number | string;
  identifier?: string;
  name?: string;
  firstName?: string;
  recipientType?: RecipientType;
  onlyCompleted?: boolean;
  lastName?: string;
  pageNo?: number;
  pageSize?: number;
  sortBy?: string;
  userId?: number;
  namePart?: string;
  sortDirection?: 'ASC' | 'DESC';
}

export interface Client {
  clientCompanyName: string;
  clientContactName: string;
  clientId: number;
  isSendOfferAvailable: boolean;
  ridesPerQuarter: number;
  supplierContractClientComment: string;
  supplierContractCreatedAt: Date;
  supplierContractDiscountPct: number;
  supplierContractEndDate: Date;
  supplierContractId: number;
  totalHours: number;
  totalKm: number;
}

export interface ClientContractPayload {
  discountPct?: number;
  endDate?: string;
  operatorComment?: string;
  newStatus?: ContractStatus;
}

export interface AlertsQueryFilter extends QueryFilter {
  history: boolean;
}

export interface PricingData {
  id: number;
  seatsRange: SeatsRangeEnum | '' | null;
  vehicleClass: VehicleClass | '' | null;
  daysOfWeek: DaysOfWeek[];
  adjustmentPct: number;
  validFrom: string;
  validTo: string;
  startTime: string;
  endTime: string;
  type: 'MARKUP' | 'DISCOUNT';
}

export interface ActiveRides {
  elements: {
    bookingNumber: string;
    driverId: number;
    driverName: string;
    licensePlate: string;
    orderId: number;
    startedAt: Date;
    vehicleId: number;
  }[];
  pageCount: number;
  pageNo: number;
  pageSize: number;
  totalElements: number;
}

export interface LocalTime {
  hour: number;
  minute: number;
  nano?: number;
  second?: number;
}

export interface OfferLocation {
  coords: {
    lat: number;
    lon: number;
  };
  locationText: string;
  placeId: string;
  address: string;
}

export interface Stop {
  distanceKmFromPrevious: number;
  location: Location;
  minutesFromPrevious: number;
  name?: string;
  street?: string;
  id?: string;
  time?: string;
}

interface Schedule {
  daysOfWeek: DaysOfWeek[];
  endsOn: string | null;
  startsOn: string | null;
}

export interface Departure {
  clientDepartureTime: string;
  clientReturnTime?: string;
  licensePlate: string;
  schedule: Schedule;
  id?: string;
  vehicle?: Vehicle;
}

export interface StopBasedItineraryRequest {
  departures: [{ clientDepartureTime: string }];
  passengerCount: number;
  stops: OfferLocation[];
}

export interface StopBasedItinerary {
  departures: Departure[];
  encodedPolylines: string[];
  stops: Stop[];
}

export interface CorporateCharterRequest {
  clientId: number;
  contactInfo: ContactInfo;
  departures: DeparturesSchemaType[];
  encodedPolylines: string[];
  lineName: string;
  passengerCount: number;
  ridePriceExclVat: number;
  secondaryContactInfo?: ContactInfo;
  stops: CorporateCharterScheduleStopsSchemaType[];
}

export interface CorporateCharter {
  id: number;
  client?: Client;
  contactInfo: ContactInfo;
  departures: Departure[];
  lineName: string;
  operatorId: number;
  operatorName?: string;
  operatorPhoneNumber?: string;
  passengerCount: number;
  ridePriceExclVat: number;
  secondaryContactInfo?: ContactInfo;
  stops: Stop[];
}

export interface OfferLeg {
  endLocation: OfferLocation | null;
  startLocation: OfferLocation | null;
  startTime: Date | null;
}

export interface OfferPayload {
  licensePlate: string;
  driverLanguage: string;
  duration?: number;
  endLocation?: PlaceAutocompleteResult;
  extras: string[];
  legs: MultiStopLegsSchemaType[];
  passengerCount: number | string;
  returnTime: Date | null;
  specialLuggageCount?: number;
  specialLuggageType?: string;
  startLocation?: PlaceAutocompleteResult;
  startTime: Date | null;
  type: string;
}

export type ModifiedOfferPayload = OfferPayload & {
  startLocation?: any;
  endLocation?: any;
};

export interface LocationParams {
  maxLat: number;
  maxLon: number;
  minLat: number;
  minLon: number;
}

export interface GoogleConf {
  enabledCountries: string[];
  feApiKey: string;
}

export interface VehicleLocation {
  coords: {
    lat: number;
    lon: number;
  };
  licensePlate: string;
  vehicleId: number;
  orderId: number;
  rideId: number;
}

export interface DriverCandidate {
  id: number;
  value: string;
}

export interface VehicleCandidate {
  id: number;
  value: string;
}

export interface RideWithScheduleItem {
  changedScheduleItems: ScheduleElement[];
  ride: Ride;
  scheduleItem: ScheduleElement;
}

export interface FormOption<T> {
  key: keyof T;
  label: string;
  initialValue: null | boolean | string | number | unknown[] | Record<string, unknown>;
  type?: string;
  required?: boolean;
  multiline?: boolean;
  description?: string;
  helperText?: string;
  placeholder?: string;
}

export interface ExtendedFormOption<T> {
  key: keyof T;
  label: string;
  initialValue: boolean | string | number | unknown[] | PlaceAutocompleteResult;
  type?: string;
  placeholder?: string;
  description?: string;
  gridSize?: number;
}

export interface AddElementPayload {
  operatorId: number;
  values: any;
}

export interface TeamMember extends ContactInfo {
  id: number;
  role: UserRole;
}

export interface OperatorCostInput {
  bySeats: OperatorCostBySeats[];
  common: OperatorCostCommon[];
  operatorId: number;
}

export interface CurrentLocation {
  placeId: string;
  locationText: string;
}

export interface MultiStopLeg {
  location: PlaceAutocompleteResult | null;
  startTime: Date | null;
  id?: string;
}

export interface Stopover {
  location: PlaceAutocompleteResult | null;
  id: string | number;
}

export interface BookBusFormFieldKeys {
  type: string;
  startLocation: PlaceAutocompleteResult;
  driverLanguage: string;
  endLocation: PlaceAutocompleteResult;
  passengerCount: number;
  return: boolean;
  startTime: Date;
  startClockTime: Date;
  returnTime: Date;
  returnClockTime: Date;
  duration: Date;
  legs: MultiStopLegsSchemaType[];
}

export interface OperatorCostBySeats {
  adminCostsPerHour: number;
  dailyMargin: number;
  departureCost: number;
  driverHourlyRate: number;
  fuelConsumptionLitersPerKm: number;
  seatsFrom: number;
  seatsTo: number;
  vehicleCostsPerHour: number;
  vehicleCostsPerKm: number;
  standardDiscountPct: number;
  executiveDiscountPct: number;
}
export interface OperatorCostCommon {
  dailyAllowance: number;
  executiveRatePctLarge: number;
  executiveRatePctSmall: number;
  fuelPriceLiterExclVat: number;
}

export interface DriverNotification {
  to: string;
  recipientType: string;
  subject: string;
  content: string;
}

export interface OperatorDetails {
  address: string;
  approved: boolean;
  brandName: string;
  companyVat?: string;
  contactEmail: string;
  contactPhoneNumber: string;
  country: string;
  description: string;
  id: number;
  legalName: string;
  modules: string[];
  operatingLicense: string;
  regCode: string;
  termsAcceptedTime: string;
  website: string;
}

export interface PricingDetails {
  adjustmentPct: number;
  adjustmentType: string;
  adminCost: number;
  clientEndTime: string;
  clientStartTime: string;
  days: number;
  departureCost: number;
  driverEndTime: string;
  driverStartTime: string;
  driversCost: number;
  endAddress: string;
  finalPriceExclVat: number;
  finalPriceInclVat: number;
  fuelCost: number;
  hours: number;
  mileageKm: number;
  netProfitMargin: number;
  seatsFrom: number;
  seatsTo: number;
  startAddress: string;
  sumOfTotalCosts: number;
  unadjustedPriceExclVat: number;
  unadjustedPriceInclVat: number;
  vehicleClass: string;
  vehicleMileageCost: number;
  vehicleTimeCost: number;
  vehicleTotalCost: number;
}

export interface OperatorCalculatorResponse {
  adjustmentPct: number;
  adjustmentType: PricingAdjustmentTypeEnum;
  finalPriceExclVat: number;
  finalPriceInclVat: number;
  netProfitMargin: number;
  unadjustedPriceExclVat: number;
  unadjustedPriceInclVat: number;
  seatsFrom: number;
  seatsTo: number;
  sumOfTotalCosts: number;
  vehicleClass: VehicleClass;
  vehicleClassAdjustmentPct: number;
  costs: {
    adminCost: number;
    departureCost: number;
    driversCost: number;
    fuelCost: number;
    vehicleMileageCost: number;
    vehicleTimeCost: number;
  };
  days: number;
  hours: number;
  mileageKm: number;
}

export interface OperatorKeys {
  address: string;
  brandName: string;
  companyVat?: string;
  contactEmail: string;
  contactPhoneNumber: string;
  emergencyEmail?: string;
  emergencyPhoneNumber: string;
  legalName: string;
  name?: string;
  operatingLicense: string;
  regCode: string;
  termsAcceptedTime?: string;
  website?: string;
}

export interface SignUpKeys {
  operator: OperatorKeys;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  name: string;
  operatingLicense: string;
  address: string;
  companyVat?: string;
  regCode: string;
  password: string;
  confirmPassword: string;
  currentPassword: string;
  website: string;
  approved: boolean;
  contactEmail: string;
  contactInfo: ContactInfo;
  contactPhoneNumber: string;
  brandName: string;
  country: string;
  description: string;
  emergencyPhoneNumber: string;
  emergencyEmail?: string;
  legalName: string;
  modules: string[];
  termsAcceptedTime: string;
  termsAccepted: boolean;
}

export interface UserInvite {
  email: FormOption<any>;
  role: FormOption<any>;
}
export interface InvitationForm {
  user: UserInvite[];
}

export interface AccountSettings {
  automaticAccept: boolean;
  acceptSameDay: boolean;
  sameDayTime: string;
  updates: boolean;
  featureAnnouncements: boolean;
  mobiferOffers: boolean;
  deleteAccount: boolean;
}

export interface RegistrationResponse {
  userId: number;
  contactInfo?: ContactInfo;
  jwt: string;
  refreshToken: string;
}

export interface OperatorRegistrationPayload {
  contactInfo: ContactInfo;
  operator: OperatorKeys;
  password: string;
  confirmPassword: string;
  termsAccepted: boolean;
  lang: string;
  accessToken?: string;
}

export interface NewMessageGroupPayload {
  name: string;
  description?: string;
  members: number[];
}

export type FormStep = {
  label: string;
  description: string;
};

export type EditBookingModalFormFields = Pick<
  OrderV2,
  | 'groupLead'
  | 'rides'
  | 'signText'
  | 'pickUpLocationMessage'
  | 'passengerCount'
  | 'specialLuggageCount'
  | 'specialLuggageType'
  | 'internalInfo'
  | 'rideDetailsFileUrl'
  | 'signFileUrl'
  | 'driverAccommodationType'
> & {
  specialLuggage: boolean;
  rideDetailsFile?: UploadFile;
  signFile?: UploadFile;
};

export const stopover = {
  location: null,
  id: 0,
};

export enum SessionStorage {
  PREVIOUS_PAGE = 'previous',
}

export enum SchedulerTypeEnum {
  BUS_SCHEDULER = 'bus-scheduler',
  DRIVER_SCHEDULER = 'driver-scheduler',
  DRIVER_WORK_SCHEDULER = 'driver-work-scheduler',
}

export enum AddEventTypeEnum {
  BUS = 'bus',
  RIDE = 'ride',
  OFFER = 'offer',
  REGULAR_TRIP = 'trip',
  VACATION = 'vacation',
  REPAIR = 'repair',
  DRIVER = 'driver',
  OFFLINE = 'offline',
}

export enum ModifyRideTypeEnum {
  END_RIDE = 'END_RIDE',
}

interface PredictionSubstring {
  offset: number;
  length: number;
}

interface StructuredFormatting {
  main_text: string;
  secondary_text: string;
  main_text_matched_substrings?: readonly PredictionSubstring[];
}
export interface AutocompletePlacesResponse {
  address: string;
  structured_formatting?: StructuredFormatting;
  place_id: string;
}

export type AddressType =
  /** indicates a precise street address. */
  | 'street_address'
  /** indicates a named route (such as "US 101"). */
  | 'route'
  /** indicates a major intersection, usually of two major roads. */
  | 'intersection'
  /** indicates a political entity. Usually, this type indicates a polygon of some civil administration. */
  | 'political'
  /** indicates the national political entity, and is typically the highest order type returned by the Geocoder. */
  | 'country'
  /**
   * indicates a first-order civil entity below the country level. Within the United States, these administrative levels are states.
   * Not all nations exhibit these administrative levels. In most cases, `administrative_area_level_1` short names will closely match
   * ISO 3166-2 subdivisions and other widely circulated lists; however this is not guaranteed as our geocoding results are based
   * on a variety of signals and location data.
   */
  | 'administrative_area_level_1'
  /**
   * indicates a second-order civil entity below the country level. Within the United States, these administrative levels are counties.
   * Not all nations exhibit these administrative levels.
   */
  | 'administrative_area_level_2'
  /**
   * indicates a third-order civil entity below the country level. This type indicates a minor civil division.
   * Not all nations exhibit these administrative levels.
   */
  | 'administrative_area_level_3'
  /**
   * indicates a fourth-order civil entity below the country level. This type indicates a minor civil division.
   * Not all nations exhibit these administrative levels.
   */
  | 'administrative_area_level_4'
  /**
   * indicates a fifth-order civil entity below the country level. This type indicates a minor civil division.
   * Not all nations exhibit these administrative levels.
   */
  | 'administrative_area_level_5'
  /** indicates a commonly-used alternative name for the entity. */
  | 'colloquial_area'
  /** indicates an incorporated city or town political entity. */
  | 'locality'
  /**
   * indicates a specific type of Japanese locality, to facilitate distinction between multiple locality components within a
   * Japanese address.
   */
  | 'ward'
  /**
   * indicates a first-order civil entity below a locality. For some locations may receive one of the additional types:
   * `sublocality_level_1` to `sublocality_level_5`. Each sublocality level is a civil entity. Larger numbers indicate a smaller
   * geographic area.
   */
  | 'sublocality'
  /** indicates a named neighborhood */
  | 'neighborhood'
  /** indicates a named location, usually a building or collection of buildings with a common name */
  | 'premise'
  /**
   * indicates a first-order entity below a named location, usually a singular building within a collection of buildings with a
   * common name.
   */
  | 'subpremise'
  /** indicates a postal code as used to address postal mail within the country. */
  | 'postal_code'
  /** indicates a prominent natural feature. */
  | 'natural_feature'
  /** indicates an airport. */
  | 'airport'
  /** indicates a named park. */
  | 'park'
  /**
   * indicates a named point of interest. Typically, these "POI"s are prominent local entities that don't easily fit in another category,
   * such as "Empire State Building" or "Statue of Liberty".
   */
  | 'point_of_interest';
export interface PlaceAutocompleteResult {
  /**
   * contains the human-readable name for the returned result.
   * For `establishment` results, this is usually the business name.
   */
  description: string;
  /**
   * is a textual identifier that uniquely identifies a place.
   * To retrieve information about the place, pass this identifier in the `placeId` field of a Places API request.
   */
  place_id: string;
  /**
   * contains an array of terms identifying each section of the returned description
   * (a section of the description is generally terminated with a comma).
   */
  terms: PredictionSubstring[];
  /**
   * contains an array of types that apply to this place.
   * For example: `[ "political", "locality" ]` or `[ "establishment", "geocode" ]`.
   */
  types: AddressType[];
  /**
   * contains an array with `offset` value and `length`. These describe the location of
   * the entered term in the prediction result text, so that the term can be highlighted if desired.
   */
  matched_substrings: PredictionSubstring[];
  /** contains details on the prediction. */
  structured_formatting: StructuredFormatting;
}

export type GoogleConfig = {
  enabledCountries: string[];
  feApiKey: string;
};

export type OffersSettings = {
  cityHireDuration: Record<string, string>;
  maxLegs: number;
  minAdvanceTime: string;
  vehicleExtras: Partial<VehicleExtrasEnum[]>;
};

export type DriverForSchedule = {
  firstName: string;
  id: number;
  lastName: string;
};

export type VehicleForSchedule = {
  id: number;
  licensePlate: string;
  make: string;
  seats: number;
  seatsRange: SeatsRangeEnum;
};

export type ScheduleItem = {
  bookingNumber?: string;
  bookingStatus?: BookingStatus;
  clientEndAddress?: string;
  clientEndTime?: string;
  clientStartAddress?: string;
  clientStartTime?: string;
  comment?: string;
  endTime: string;
  features: FeaturesEnum[];
  id?: number;
  itemType: ScheduleItemType;
  movable: boolean;
  operatorId: number;
  orderId?: number;
  orderType?: RideOfferType;
  ownerId: number;
  ownerType: OwnerType;
  rideId?: number;
  rideNumber?: number;
  startTime: string;
};

export type ScheduleFilterStreamingResponse = {
  drivers: DriverForSchedule[];
  end: string;
  operatorId: number;
  scheduleItems: ScheduleItem[];
  start: string;
  vehicles: VehicleForSchedule[];
};

export type BookingItem = { bookingNumber: string; rideNumber: number } | undefined;

export type SelectedJoinEventsProps = {
  from: ScheduleElement;
  to: ScheduleElement;
};

export type PlanDto = {
  features: Array<
    | 'AUTOMATIC_OFFERS'
    | 'LAST_MINUTE_OFFERS'
    | 'OPERATOR_PRICING'
    | 'OPERATOR_OFFER'
    | 'COMPETING_OFFERS'
    | 'CLIENT_FEEDBACK_REPORT'
    | 'DRIVER_APP'
    | 'SUPPLIER_MANAGEMENT'
    | 'ALERTS'
    | 'CORPORATE_CHARTER'
  >;
  id: number;
  kmPriceExclVat?: number;
  monthlyPriceExclVat: number;
  name: string;
  yearlyPriceExclVat?: number;
};

export type OperatorPlanDto = {
  plan?: PlanDto;
  billingFrequency: BillingFrequency;
  operatorId: number;
  validFrom: string;
};

export type MileageRevenueSummary = {
  currentMonthCompletedKm: number;
  currentMonthCompletedOrderCount: number;
  currentMonthCompletedRevenue: number;
  currentMonthPlannedKm: number;
  currentMonthPlannedOrderCount: number;
  currentMonthPlannedRevenue: number;
  currentMonthPlannedPriceExclVat?: number;
  currentMonthCompletedPriceExclVat?: number;
  operatorId: number;
  kmPriceExclVat?: number;
  previousMonthCompletedKm: number;
  previousMonthCompletedOrderCount: number;
  previousMonthCompletedRevenue: number;
};
